import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Typography
} from '@mui/material';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  THRESHOLDS,
  selectCascadeCandidates
} from './analysis/cascadeScreening';

// Toolbar action: first-pass-select family members eligible for cascade
// screening for one disease, then hand off to the existing multi-select →
// Bulk edit flow. Consumes the analyzer's cascade candidates; changes nothing
// on the pedigree until Apply. Hidden when Clinical Insights is off (no cascade
// data exists then).
export default function CascadeScreeningButton({
  analysis,
  diagnoses = [],
  labelsSet,
  members = [],
  onApply,
  accent = '#2ba9bf'
}) {
  const t = useAutoTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  // Session-persistent settings (component stays mounted across opens).
  const [diseaseId, setDiseaseId] = useState(null);
  const [thresholdKey, setThresholdKey] = useState('p50');
  const [excludeMinors, setExcludeMinors] = useState(false);
  const [excludeTested, setExcludeTested] = useState(true);

  const cascadeCandidates = analysis?.family?.cascadeCandidates || [];

  const visibleDiseases = useMemo(
    () =>
      (diagnoses || []).filter(
        (d) =>
          labelsSet?.diseases !== false &&
          labelsSet?.diseasesMap?.[d.id] !== false
      ),
    [diagnoses, labelsSet]
  );

  // Single visible disease → use it; otherwise honour the dropdown choice (only
  // if it's still visible).
  const effectiveDiseaseId =
    visibleDiseases.length === 1
      ? visibleDiseases[0].id
      : diseaseId && visibleDiseases.some((d) => d.id === diseaseId)
      ? diseaseId
      : null;
  const effectiveDiseaseName = visibleDiseases.find(
    (d) => d.id === effectiveDiseaseId
  )?.name;

  const memberByPid = useMemo(() => {
    const map = {};
    for (const m of members || []) map[String(m.pid)] = m;
    return map;
  }, [members]);

  const minProbability =
    THRESHOLDS.find((x) => x.key === thresholdKey)?.min ?? 0.5;

  const filterArgs = (min) => ({
    cascadeCandidates,
    diseaseId: effectiveDiseaseId,
    minProbability: min,
    excludeMinors,
    excludeAlreadyTested: excludeTested,
    memberByPid
  });

  const candidates = useMemo(
    () =>
      effectiveDiseaseId
        ? selectCascadeCandidates(filterArgs(minProbability))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cascadeCandidates,
      effectiveDiseaseId,
      minProbability,
      excludeMinors,
      excludeTested,
      memberByPid
    ]
  );

  const countFor = (min) =>
    effectiveDiseaseId ? selectCascadeCandidates(filterArgs(min)).length : 0;

  const memberLabel = (pid) => {
    const m = memberByPid[String(pid)];
    if (!m) return `#${pid}`;
    const name = [m.firstName, m.lastName].filter(Boolean).join(' ').trim();
    if (name) return name;
    if (m.proBandId) return t('UPN 1');
    return m.upn != null ? t('UPN {{upn}}', { upn: m.upn }) : `#${pid}`;
  };

  // Resolve back to the members' native pid type so the Set membership checks
  // downstream (Bulk edit filter, selection chip) match.
  const selectedPids = candidates
    .map((c) => memberByPid[String(c.pid)])
    .filter(Boolean)
    .map((m) => m.pid);

  const close = () => setAnchorEl(null);
  const handleApply = () => {
    onApply(selectedPids);
    close();
  };

  const applyDisabled = !effectiveDiseaseId || candidates.length === 0;

  return (
    <>
      <Tooltip
        title={t(
          'Select family members eligible for cascade screening based on inheritance probability.'
        )}
      >
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <ChecklistIcon fontSize="small" sx={{ color: accent }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '.95rem', mb: 1 }}>
            {t('Cascade screening candidates')}
          </Typography>

          {/* Target disease */}
          {visibleDiseases.length === 1 ? (
            <Typography sx={{ fontSize: '.8rem', mb: 1 }}>
              {t('Cascade screening for:')}{' '}
              <strong>{effectiveDiseaseName}</strong>
            </Typography>
          ) : (
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <Typography
                sx={{ fontSize: '.8rem', mb: 0.5, color: 'text.secondary' }}
              >
                {t('Cascade screening for:')}
              </Typography>
              <Select
                value={effectiveDiseaseId || ''}
                displayEmpty
                onChange={(e) => setDiseaseId(e.target.value)}
                renderValue={(v) =>
                  v
                    ? visibleDiseases.find((d) => d.id === v)?.name
                    : t('Select a disease')
                }
              >
                {visibleDiseases.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Threshold */}
          <Typography
            sx={{
              fontSize: '.72rem',
              fontWeight: 600,
              color: 'text.secondary'
            }}
          >
            {t('Probability threshold')}
          </Typography>
          <RadioGroup
            value={thresholdKey}
            onChange={(e) => setThresholdKey(e.target.value)}
          >
            {THRESHOLDS.map((x) => (
              <FormControlLabel
                key={x.key}
                value={x.key}
                control={<Radio size="small" />}
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '.8rem' } }}
                label={`${t(x.labelKey)} (${countFor(x.min)})`}
              />
            ))}
          </RadioGroup>

          <Divider sx={{ my: 1 }} />

          {/* Exclusions */}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={excludeTested}
                onChange={(e) => setExcludeTested(e.target.checked)}
              />
            }
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '.8rem' } }}
            label={t('Exclude already-tested individuals')}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={excludeMinors}
                onChange={(e) => setExcludeMinors(e.target.checked)}
              />
            }
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '.8rem' } }}
            label={t('Exclude minors (under 18)')}
          />
          <Typography
            sx={{ fontSize: '.68rem', color: 'text.disabled', mt: 0.5 }}
          >
            {t(
              'Already excluded: affected individuals, confirmed carriers, and deceased.'
            )}
          </Typography>
          <Typography
            sx={{ fontSize: '.68rem', color: 'text.disabled', mt: 0.25 }}
          >
            {t(
              'Based on degree of relationship to affected / known-carrier individuals.'
            )}
          </Typography>

          {/* Live summary */}
          <Box
            aria-live="polite"
            sx={{
              mt: 1,
              p: 1,
              bgcolor: 'rgba(46,55,71,0.04)',
              borderRadius: 1
            }}
          >
            <Typography sx={{ fontSize: '.8rem', fontWeight: 600 }}>
              {!effectiveDiseaseId
                ? t('Select a disease to see candidates')
                : candidates.length === 1
                ? t('1 individual will be selected')
                : t('{{count}} individuals will be selected', {
                    count: candidates.length
                  })}
            </Typography>
            {candidates.length > 0 && (
              <Box sx={{ maxHeight: 96, overflowY: 'auto', mt: 0.5 }}>
                {candidates.map((c) => (
                  <Typography
                    key={c.pid}
                    sx={{ fontSize: '.72rem', color: 'text.secondary' }}
                  >
                    {memberLabel(c.pid)} ·{' '}
                    {Math.round((c.priorProbability || 0) * 100)}%
                  </Typography>
                ))}
              </Box>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Tooltip
              title={
                applyDisabled
                  ? t('No matching candidates for the current settings.')
                  : ''
              }
            >
              <span style={{ flex: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={applyDisabled}
                  onClick={handleApply}
                  sx={{ bgcolor: accent, '&:hover': { bgcolor: '#22919f' } }}
                >
                  {t('Apply selection')}
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" onClick={close}>
              {t('Cancel')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
